import { ReactElement, useMemo } from "react";

import {
    LoopExpanded,
    calculateHistoricalLoopProfitLoss,
    LoopPositionExpanded,
    LoopscalePointsAction,
    calculateUnwindStats,
    isLoanActive,
    sumLoopPositionUsdContributions,
    BsMetaUtil,
    useLoopCollateralPrice,
    useUnwindQuoteAndIxs,
    ExternalPointReward,
    useFlashPerps,
    isLstLoop,
    calculateLoopOracleProfitLoss,
    useLoopRecentStartTime,
    MIN_TIME_FOR_RECENT_START,
    calculateOracleProfitLossStats,
    isPtToken,
    getDerivedLoopAssetMints,
    getZcLedger,
    getZcCollateral,
    LoanRepayType,
    isHistoricalPrincipalWithdrawal,
    getLeverageLoopDirection
} from "@bridgesplit/abf-react";
import { LOOP_DETAIL_SLUG } from "app/constants";
import { Box } from "@mui/material";
import {
    Column,
    METEORA_SVG,
    Row,
    SPACING,
    Text,
    TextButton,
    TextSkeleton,
    Tooltip,
    TooltipText,
    useAppPalette,
    Icon,
    TextVariant,
    TagText,
    TextExternalLink,
    Span
} from "@bridgesplit/ui";
import {
    formatPercent,
    percentUiToDecimals,
    formatSeconds,
    calculatePercentChange,
    formatUsd,
    ALLOWED_FEATURES
} from "@bridgesplit/utils";
import { LoopFilterTags, LoopRouteType, Protocol, TokenListMetadata } from "@bridgesplit/abf-sdk";
import { SwapHoriz } from "@mui/icons-material";

import { getTokenImageSize, TokenImage, TokenSize, TokenSolscanLink } from "./asset";
import { DEFAULT_SLIPPAGE } from "./slippage";
import { AlertConfig, getMarketHoursAlertConfig, ProfitLossText } from "./util";
import { RewardsLoopsApy } from "../points";
import { SocialShareCta, useLoopSocialShareProps } from "../portfolio";

export enum LoopDisplayType {
    UsdCostBasis,
    TokenCostBasis
}

export function getLoopPath(loopExpanded: LoopExpanded) {
    return `${LOOP_DETAIL_SLUG}/${loopExpanded.vaultIdentifier}`;
}

export function LoopNewTag() {
    return (
        <TagText borderRadius={100} color="secondary">
            New
        </TagText>
    );
}

export function LoopImage({
    loopExpanded,
    size: propsSize,
    offset = -0.25,
    badgeSize = "sm"
}: {
    loopExpanded: LoopExpanded | undefined;
    size: TokenSize;
    badgeSize?: TokenSize;
    offset?: number;
}) {
    const size = getTokenImageSize(propsSize);
    const { background } = useAppPalette();

    const [primaryToken, secondaryToken] = [loopExpanded?.principalToken, loopExpanded?.collateralToken];

    return (
        <Box sx={{ position: "relative", width: size, height: size }}>
            <TokenImage
                size={badgeSize}
                sx={{
                    position: "absolute",
                    bottom: SPACING * offset,
                    right: SPACING * offset,
                    zIndex: 1,
                    borderRadius: "100%",
                    border: `2px solid ${background}`,
                    boxSizing: "content-box"
                }}
                metadata={primaryToken}
            />
            <TokenImage size={propsSize} metadata={secondaryToken} />
        </Box>
    );
}

const PROTOCOL_TO_EXTERNAL_POINT_REWARDS: Record<Protocol, ExternalPointReward[]> = {
    [Protocol.Meteora]: [ExternalPointReward.Meteora],
    [Protocol.Exponent]: [],
    [Protocol.RateX]: [ExternalPointReward.RateX],
    [Protocol.Sanctum]: [],
    [Protocol.FlashTrade]: [ExternalPointReward.Flash],
    [Protocol.Fragmetric]: [ExternalPointReward.Fragmetric],
    [Protocol.Adrastea]: [ExternalPointReward.Adrastea],
    [Protocol.Stabble]: [ExternalPointReward.Stabble],
    [Protocol.Solayer]: [ExternalPointReward.Solayer],
    [Protocol.Onre]: [ExternalPointReward.Onre]
};

export function getLoopExternalPointRewards(loopExpanded: LoopExpanded | undefined): ExternalPointReward[] | undefined {
    if (!loopExpanded) {
        return undefined;
    }

    const protocols = loopExpanded.loopVault.protocols;
    if (!protocols || protocols.length === 0) {
        return [];
    }

    const externalPointRewards: ExternalPointReward[] = [];

    for (const protocol of protocols) {
        const rewards = PROTOCOL_TO_EXTERNAL_POINT_REWARDS[protocol];
        if (rewards && rewards.length > 0) {
            externalPointRewards.push(...rewards);
        }
    }

    return externalPointRewards;
}

export function LoopApy({
    loopExpanded,
    isFeaturedLoop = false,
    useAverage = false
}: {
    loopExpanded: LoopExpanded | undefined;
    isFeaturedLoop?: boolean;
    useAverage?: boolean;
}) {
    const maxLeverage = loopExpanded?.loopVault.maxLeverage;

    const apy = useAverage ? loopExpanded?.loopVault.wAvgApy : loopExpanded?.loopVault.maxLeveragedApyPct;
    const metadata = loopExpanded?.principalToken;

    const externalPointRewards = getLoopExternalPointRewards(loopExpanded);

    if (!loopExpanded || !metadata || !apy) {
        return null;
    }

    return (
        <RewardsLoopsApy
            apy={apy}
            externalPointRewards={externalPointRewards}
            loopscalePointsAction={LoopscalePointsAction.Borrow}
            metadata={metadata}
            collateralMetadata={loopExpanded?.collateralToken}
            maxLeverage={maxLeverage ?? 0}
            loopExpanded={loopExpanded}
            isFeatured={isFeaturedLoop}
        />
    );
}

export function useLoopPointsDetails({ loopExpanded }: { loopExpanded: LoopExpanded | undefined }) {
    const { isDarkMode } = useAppPalette();

    return useMemo(() => {
        switch (loopExpanded?.loopVault.routeType) {
            case LoopRouteType.Jup:
                return null;
            case LoopRouteType.Meteora:
                return {
                    image: METEORA_SVG,
                    backgroundColor: isDarkMode ? undefined : "#141C37",
                    name: "Meteora"
                };
            default:
                return null;
        }
    }, [isDarkMode, loopExpanded?.loopVault.routeType]);
}

export function useLoopProfitLoss(
    loopPositionExpanded: LoopPositionExpanded | undefined,
    options: { skip: boolean },
    unwindType: LoanRepayType | undefined
) {
    const isActive = useMemo(() => isLoanActive(loopPositionExpanded?.loanExpanded), [loopPositionExpanded]);
    const ledger = getZcLedger(loopPositionExpanded?.loanExpanded);
    const debtToRepay = ledger?.ledgerDebt.total;
    const loanCollateral = getZcCollateral(loopPositionExpanded?.loanExpanded);

    const { data: externalQuote, isLoading: externalQuoteLoading } = useUnwindQuoteAndIxs({
        loopPositionExpanded,
        debtToRepay,
        loanCollateral,
        collateralAmount: loanCollateral?.amount,
        loanExpanded: loopPositionExpanded?.loanExpanded,
        slippagePercentDecimals: percentUiToDecimals(DEFAULT_SLIPPAGE),
        skip: options?.skip || !isActive || !loopPositionExpanded,
        disablePoll: true,
        unwindType
    });

    const { data: jupiterPrices } = useLoopCollateralPrice({
        tokens: loopPositionExpanded?.loopExpanded
            ? [loopPositionExpanded.loopExpanded?.collateralToken, loopPositionExpanded.loopExpanded?.principalToken]
            : undefined,
        options: { skip: !loopPositionExpanded }
    });

    return useMemo(() => {
        if (!loopPositionExpanded || !jupiterPrices || externalQuoteLoading || unwindType === undefined)
            return undefined;

        // fallback on BE calculation if external quote is not available
        if (!isActive) {
            return calculateHistoricalLoopProfitLoss(loopPositionExpanded);
        }

        return calculateUnwindStats({ loopPosition: loopPositionExpanded, externalQuote, jupiterPrices, unwindType });
    }, [loopPositionExpanded, jupiterPrices, externalQuoteLoading, isActive, externalQuote, unwindType]);
}

export function useLoopMarketHoursAlertConfig(loopExpanded: LoopExpanded | undefined) {
    let isClosed: boolean | undefined;
    let alertConfig: AlertConfig | undefined;

    const mints = getDerivedLoopAssetMints(loopExpanded);

    return useMemo(() => {
        if (!mints) return { isClosed, alertConfig };
        return getMarketHoursAlertConfig(mints);
    }, [mints, isClosed, alertConfig]);
}

export function useLoopWindAlerts(loopExpanded: LoopExpanded | undefined) {
    const routeType = loopExpanded?.loopVault.routeType;
    let windDisabledWithMessage: string | undefined;

    const { data: flashPerps } = useFlashPerps({ skip: !loopExpanded || routeType !== LoopRouteType.Flash });

    if (!loopExpanded) return { windDisabledWithMessage };

    if (flashPerps && routeType === LoopRouteType.Flash) {
        const maxAumUsd = loopExpanded?.loopVault.aumLimit;
        const aumUsd =
            flashPerps.pools.find((pool) =>
                loopExpanded.collateralToken.symbol.toLocaleLowerCase().includes(pool.flpTokenSymbol.toLowerCase())
            )?.aum ?? undefined;
        const isCapExceeded = maxAumUsd && aumUsd && maxAumUsd < parseFloat(aumUsd);
        if (isCapExceeded) {
            windDisabledWithMessage = "Deposits temporarily disabled due to Flash Trade's AUM cap being exceeded";
        }
    }

    return { windDisabledWithMessage };
}

export function useLoopUnwindAlerts({
    loopPositionExpanded,
    summary,
    variant
}: {
    loopPositionExpanded: LoopPositionExpanded | undefined;
    summary: ReturnType<typeof useLoopProfitLoss>;
    variant?: "preview" | "route";
}) {
    const routeType = loopPositionExpanded?.loopExpanded?.loopVault.routeType;
    let unwindWarningMessage: string | undefined;

    if (!loopPositionExpanded || summary === undefined || !loopPositionExpanded.loopExpanded)
        return { unwindWarningMessage };

    const {
        userLoopInfo: { netApyLive }
    } = loopPositionExpanded;

    const isTemporarilyNegativePnl = !!netApyLive && netApyLive > 0 && summary.profitLossUsd < 0;

    const oraclePnl = calculateOracleProfitLossStats(loopPositionExpanded);

    if (
        isLstLoop(loopPositionExpanded.loopExpanded) &&
        summary.profitLossUsd < 0 &&
        oraclePnl &&
        oraclePnl.usdProfitLoss > 0
    ) {
        const stakeWarning =
            "Staking yield compounds with each epoch. The amount you receive reflects the LST's market value, which may not yet include the current epoch's rewards.";

        if ((variant === "route" && isTemporarilyNegativePnl) || variant === "preview") {
            unwindWarningMessage = stakeWarning;
        }
    }

    if (routeType === LoopRouteType.Meteora) {
        if (isTemporarilyNegativePnl) {
            const loopApy = formatPercent(netApyLive);
            if (variant === "route") {
                unwindWarningMessage = `You're earning ${loopApy} APY. Closing now locks in losses. Meteora may not have updated trading fees`;
            } else {
                unwindWarningMessage = `You're earning ${loopApy} APY but your P&L is temporarily negative since Meteora has a setup fee and periodically updates yields`;
            }
        }
    }

    return { unwindWarningMessage };
}

export function LoopProfitLoss({
    loopPosition,
    gainSummary,
    displayType,
    percentPosition,
    variant
}: {
    loopPosition: LoopPositionExpanded | undefined;
    gainSummary: ReturnType<typeof useLoopProfitLoss>;
    displayType: LoopDisplayType;
    percentPosition?: "left" | "right";
    variant?: TextVariant;
}) {
    const { recentStartTime, isLoading } = useLoopRecentStartTime(loopPosition);
    const isHistoricalPrincipal = isHistoricalPrincipalWithdrawal(loopPosition);

    const displayUsd = isHistoricalPrincipal || displayType === LoopDisplayType.UsdCostBasis;

    const profitLossDetails = useProfitLossDetails({
        gainSummary,
        displayUsd
    });

    const socialShareData = useLoopSocialShareProps(
        loopPosition,
        gainSummary,
        displayUsd ? LoopDisplayType.UsdCostBasis : displayType
    );

    if (!loopPosition || !gainSummary || isLoading || !profitLossDetails) return null;

    if (recentStartTime) {
        return (
            <TooltipText
                helpText={`P&L calculation delayed to resolve initial price fluctuations (check back in ${formatSeconds(
                    MIN_TIME_FOR_RECENT_START
                )})`}
                color="disabled"
            >
                Pending
            </TooltipText>
        );
    }

    const { profitLoss, profitLossPct } = profitLossDetails;

    const showTooltip = (profitLoss ?? 0) < 0 && !isLoanActive(loopPosition?.loanExpanded);

    const ProfitLoss = () => (
        <Row spacing={0.5}>
            <ProfitLossText
                variant={variant}
                percentPosition={percentPosition}
                percentChange={profitLossPct}
                profitLossUsd={displayUsd ? profitLossDetails.profitLoss : undefined}
                profitLossTokenAmount={displayUsd ? undefined : profitLossDetails.profitLoss}
                metadata={displayUsd ? undefined : loopPosition?.loopExpanded?.inputToken}
            />
            {socialShareData && ALLOWED_FEATURES.socialShare && <SocialShareCta data={socialShareData} />}
        </Row>
    );

    if (showTooltip) {
        return (
            <Tooltip title={<PnlTooltip />} reverseColors>
                <ProfitLoss />
            </Tooltip>
        );
    }

    return <ProfitLoss />;
}

export function LoopInitialDeposit({
    loopPosition,
    displayType,
    setDisplayType
}: {
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayType: (displayType: LoopDisplayType) => void;
}) {
    if (!loopPosition) return <TextSkeleton variant="body1" width="60px" />;

    const { loopExpanded, userLoopInfo } = loopPosition;
    if (!loopExpanded) return null;
    const contributions = sumLoopPositionUsdContributions(loopPosition);

    return (
        <Row spacing={0.5}>
            <TextButton
                color="disabled"
                onClick={() =>
                    setDisplayType(
                        displayType === LoopDisplayType.UsdCostBasis
                            ? LoopDisplayType.TokenCostBasis
                            : LoopDisplayType.UsdCostBasis
                    )
                }
            >
                <SwapHoriz />
            </TextButton>
            {displayType === LoopDisplayType.UsdCostBasis && (
                <Text translate="no">{formatUsd(contributions.initialCollateralUsd)} </Text>
            )}
            {displayType === LoopDisplayType.TokenCostBasis && (
                <Text translate="no">
                    {BsMetaUtil.formatAmount(loopExpanded.inputToken, userLoopInfo.initialCollateralAmount)}{" "}
                </Text>
            )}
        </Row>
    );
}

export const ORACLE_DISCREPANCY_THRESHOLD = 0.05;

export function getOracleDiffFromGainSummary(
    gainSummary: ReturnType<typeof useLoopProfitLoss>,
    loopPosition: LoopPositionExpanded | undefined
) {
    if (!gainSummary || !loopPosition) return undefined;
    const oracleDiff = gainSummary.tokenStats?.oraclePercentDifference;
    const oraclePrice = gainSummary.tokenStats?.oraclePrice;
    const quotePrice = gainSummary.tokenStats?.quotePrice;
    const collateralToken = loopPosition.loopExpanded?.collateralToken;

    if (!oraclePrice || !quotePrice || !collateralToken || oracleDiff === undefined) {
        return undefined;
    }

    return { oracleDiff, oraclePrice, quotePrice, collateralToken };
}

export function OracleFairValue({
    loopPosition,
    gainSummary
}: {
    loopPosition: LoopPositionExpanded | undefined;
    gainSummary: ReturnType<typeof useLoopProfitLoss>;
}) {
    if (!gainSummary) return <TextSkeleton variant="body1" width="60px" />;

    const oracleDiff = gainSummary.tokenStats?.oraclePercentDifference;
    const oraclePrice = gainSummary.tokenStats?.oraclePrice;
    const quotePrice = gainSummary.tokenStats?.quotePrice;
    const collateralToken = loopPosition?.loopExpanded?.collateralToken;

    // Hide tooltip if any required props are undefined
    if (!oraclePrice || !quotePrice || !collateralToken || oracleDiff === undefined) {
        return (
            <Row spacing={0.5}>
                <Text color="disabled">
                    <Icon type="help" />
                </Text>
                <Text color="caption">No quote found</Text>
            </Row>
        );
    }

    const color = oracleDiff > 0 ? "success" : "warning";

    return (
        <Tooltip
            title={
                <OracleTooltipContent
                    oraclePrice={oraclePrice}
                    quotePrice={quotePrice}
                    oracleDiff={oracleDiff}
                    collateralToken={collateralToken}
                    isWind={false}
                />
            }
            reverseColors
        >
            <Row spacing={0.5}>
                <Text color="disabled">
                    <Icon type="help" />
                </Text>
                <Text color={oracleDiff === 0 ? "caption" : color}>
                    {oracleDiff === 0 ? "No difference" : formatPercent(oracleDiff)}
                </Text>
            </Row>
        </Tooltip>
    );
}

export function OracleTooltipContent({
    oraclePrice,
    quotePrice,
    collateralToken,
    oracleDiff,
    isWind
}: {
    oraclePrice: number;
    quotePrice: number;
    collateralToken: TokenListMetadata | undefined;
    oracleDiff: number;
    isWind: boolean;
}) {
    const symbol = BsMetaUtil.getSymbol(collateralToken);

    const getStartingMessage = () => {
        if (oracleDiff > 0) {
            return `The market price is trading higher than the ${symbol}'s underlying value. `;
        } else {
            return `The market price is trading lower than the ${symbol}'s underlying value. `;
        }
    };

    const getPnLMessage = () => {
        if (isWind) {
            return oracleDiff > 0 ? "This may lead to decreased profits." : "This may lead to increased profits.";
        } else {
            return oracleDiff > 0 ? "This may lead to increased profits." : "This may lead to decreased profits.";
        }
    };

    return (
        <Column spacing={2} padding={1} sx={{ width: "fit-content" }}>
            <Text variant="body1" color="caption">
                {getStartingMessage() + getPnLMessage()}
            </Text>
        </Column>
    );
}

export function getLoopOracleProfitLoss(loopPosition: LoopPositionExpanded | undefined) {
    if (!loopPosition) return undefined;
    if (!isLoanActive(loopPosition?.loanExpanded)) {
        return calculateHistoricalLoopProfitLoss(loopPosition);
    }

    const oracleProfitLoss = calculateLoopOracleProfitLoss(loopPosition);

    return oracleProfitLoss;
}

export function LoopName({
    loopExpanded,
    variant = "h2",
    isCondensed = false
}: {
    loopExpanded: LoopExpanded | undefined;
    variant?: TextVariant;
    isCondensed?: boolean;
}) {
    if (isCondensed) {
        const collateralToken = loopExpanded?.collateralToken;

        return (
            <Row spacing={0.5}>
                <Text loading={!loopExpanded} variant={variant} fontWeight="semibold">
                    {formatLoopPtTokenName(collateralToken)}
                </Text>
                <Text variant={variant} color="disabled" fontWeight="semibold">
                    {loopExpanded?.loopVault.name.split("/")[1]}
                </Text>
            </Row>
        );
    }
    return (
        <Row spacing={0.5}>
            <Text loading={!loopExpanded} variant={variant} fontWeight="semibold">
                {loopExpanded?.loopVault.name.split("/")[0]}
            </Text>
            <Text variant={variant} color="disabled" fontWeight="semibold">
                {loopExpanded?.loopVault.name.split("/")[1]}
            </Text>
        </Row>
    );
}

export function formatLoopPtTokenName(token: TokenListMetadata | undefined) {
    if (!token?.name) return token?.symbol;

    if (isPtToken(token)) {
        if (token.name.includes("Ratex")) {
            return token.symbol.split("-")[0];
        }
        return token.symbol.split("-")[1];
    }

    return token.symbol;
}

export function TokenUsdStat({
    usdValue,
    token,
    usdPrice,
    displayAsColumn,
    warning
}: {
    usdValue: number;
    usdPrice: number;
    token: TokenListMetadata | undefined;
    displayAsColumn?: boolean;
    warning?: string;
}) {
    const tokenAmount = usdValue / usdPrice;
    const amount = BsMetaUtil.formatAmount(token, tokenAmount, { hideSymbol: false });

    return (
        <StatWithSubtext
            stat={formatUsd(usdValue)}
            subtext={amount}
            displayAsColumn={displayAsColumn}
            warning={warning}
        />
    );
}

export function TokenStat({
    tokenAmount,
    token,
    usdPrice,
    displayAsColumn,
    warning
}: {
    tokenAmount: number;
    token: TokenListMetadata | undefined;
    usdPrice: number;
    displayAsColumn?: boolean;
    warning?: string;
}) {
    const amount = BsMetaUtil.formatAmount(token, tokenAmount, { hideSymbol: false });
    const usdValue = formatUsd(tokenAmount * usdPrice);

    return <StatWithSubtext stat={usdValue} subtext={amount} displayAsColumn={displayAsColumn} warning={warning} />;
}

export function StatWithSubtext({
    stat,
    subtext,
    statVariant = "h3",
    subtextVariant = "body2",
    displayAsColumn,
    warning
}: {
    stat: string | JSX.Element;
    subtext?: string;
    statVariant?: "h3" | "h4";
    subtextVariant?: "body2" | "caption";
    displayAsColumn?: boolean;
    warning?: string;
}) {
    const baseStat =
        typeof stat === "string" ? (
            <Text variant={statVariant} svgColor={warning ? "warning" : undefined}>
                {stat}
                {warning && <Icon type="warning" />}
            </Text>
        ) : (
            stat
        );

    const statWrapped = warning ? <Tooltip title={warning}>{baseStat}</Tooltip> : baseStat;

    const content = (
        <>
            {statWrapped}
            {subtext && (
                <Text variant={subtextVariant} color="caption">
                    {subtext}
                </Text>
            )}
        </>
    );

    return displayAsColumn ? <Row spacing={1}>{content}</Row> : <Column spacing={0}>{content}</Column>;
}

export function PnlTooltip() {
    return (
        <Column spacing={1.5} padding={0.5}>
            <Text variant="caption">
                PnL may be temporarily negative due to pricing mechanics or may reflect real losses if the market moved
                against your position.
            </Text>
            <Text variant="caption" sx={{ fontWeight: 500 }}>
                <TextExternalLink
                    isPrimary
                    sx={{ alignItems: "center" }}
                    href={`https://docs.loopscale.com/using-loopscale/loop#why-is-my-pnl-negative%3F`}
                >
                    Why is my PnL negative?
                    <Icon sx={{ color: "secondary", ml: 0.5 }} type="external-link" />
                </TextExternalLink>
            </Text>
        </Column>
    );
}

export function useProfitLossDetails({
    gainSummary,
    displayUsd
}: {
    gainSummary: ReturnType<typeof useLoopProfitLoss>;
    displayUsd: boolean;
}) {
    if (!gainSummary) return undefined;

    const {
        contributions: { initialCollateralUsd, initialCollateralAmount },
        profitLossUsd,
        profitLossTokenAmount
    } = gainSummary;
    const initialDeposit = displayUsd ? initialCollateralUsd : initialCollateralAmount;
    const profitLoss = displayUsd ? profitLossUsd : profitLossTokenAmount;
    const profitLossPct = calculatePercentChange(initialDeposit, initialDeposit + (profitLoss ?? 0));

    return {
        displayUsd,
        initialDeposit,
        profitLoss,
        profitLossPct
    };
}

export function getLoopDescription(loopExpanded: LoopExpanded | undefined): React.ReactElement | undefined {
    if (!loopExpanded) return undefined;

    const { tags } = loopExpanded.loopVault;
    const { collateralToken, principalToken } = loopExpanded;
    const defaultDescription = (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />
        </Span>
    );

    const leverageDescription = getLeverageLoopDescription(collateralToken, principalToken, loopExpanded);
    if (leverageDescription) return leverageDescription;

    if (!tags) {
        return defaultDescription;
    }

    // Find the first matching description function
    for (const tag of tags) {
        const descriptionFn = LOOP_DESCRIPTIONS[tag];
        if (descriptionFn) {
            return descriptionFn(collateralToken, principalToken);
        }
    }

    return defaultDescription;
}

function getLeverageLoopDescription(
    collateralToken: TokenListMetadata,
    principalToken: TokenListMetadata,
    loopExpanded: LoopExpanded
): React.ReactElement | undefined {
    const direction = getLeverageLoopDirection(loopExpanded);

    if (direction === "Long") {
        return (
            <Span>
                This Loop gives leveraged long exposure to <TokenSolscanLink token={collateralToken} />, which follows
                the price of the underlying asset.
            </Span>
        );
    } else if (direction === "Short") {
        return (
            <Span>
                This Loop gives leveraged short exposure to <TokenSolscanLink token={principalToken} />, which follows
                the price of the underlying asset.
            </Span>
        );
    }
    return undefined;
}

/**
 * @description
 * This is a map of loop tags to their description.
 * The description is a function that takes in the collateral token, principal token, protocol, and secondary color.
 * The function returns a React element that is the description of the loop. Must live in the TSX file since we are rendering external link components.
 */
type LoopDescriptionFn = (
    collateralToken: TokenListMetadata,
    principalToken?: TokenListMetadata,
    protocol?: Protocol,
    secondary?: string
) => ReactElement;

export const LOOP_DESCRIPTIONS: Record<LoopFilterTags, LoopDescriptionFn> = {
    [LoopFilterTags.SolPegged]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a liquid staking token
            that earns staking rewards.
        </Span>
    ),

    [LoopFilterTags.Directional]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a directional position.
        </Span>
    ),

    [LoopFilterTags.Farming]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a farming strategy that
            amplifies rewards.
        </Span>
    ),

    [LoopFilterTags.StablecoinPegged]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a stablecoin strategy.
            Because both assets are dollar-linked, liquidation risk is low.
        </Span>
    ),

    [LoopFilterTags.DexLp]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a DEX liquidity position
            that earns trading fees.
        </Span>
    ),

    [LoopFilterTags.FixedYield]: (collateralToken, principalToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a fixed-yield token that
            redeems into <TokenSolscanLink token={principalToken} /> at maturity. Closing early may incur losses.
        </Span>
    ),

    [LoopFilterTags.Rwa]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a real world asset.
        </Span>
    ),

    [LoopFilterTags.Leverage]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, which follows the price
            of the underlying asset.
        </Span>
    ),

    [LoopFilterTags.CLMM]: (collateralToken) => (
        <Span>
            This Loop gives leveraged exposure to <TokenSolscanLink token={collateralToken} />, a CLMM position that
            earns trading fees.
        </Span>
    )
};
